import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import {
  SupportTicket,
  SupportTicketStatus,
} from 'src/app/models/support-ticket.model';
import { SupportTicketService } from 'src/app/services/support-ticket.service';
import {
  SUPPORT_TICKET_PAGE_SIZE,
  SUPPORT_TICKET_STATUS_OPTIONS,
} from './support-ticket.constants';

@Component({
  selector: 'app-support-ticket-list',
  templateUrl: './support-ticket-list.component.html',
  styleUrls: ['./support-ticket-list.component.scss'],
})
export class SupportTicketListComponent implements OnInit, OnDestroy {
  private static readonly PAGE_SIZE_STORAGE_KEY = 'support-ticket-list-page-size';
  private static readonly PAGE_NUMBER_STORAGE_KEY = 'support-ticket-list-page-number';

  displayedColumns: string[] = [
    'ticketNo',
    'title',
    'description',
    'status',
    'createdOn',
    'action',
  ];
  readonly pageSize = SUPPORT_TICKET_PAGE_SIZE;
  readonly pageSizeOptions = [5, 10, 20, 50];
  readonly statusOptions = SUPPORT_TICKET_STATUS_OPTIONS;
  readonly searchControl = new FormControl('');

  dataSource = new MatTableDataSource<SupportTicket>([]);
  tickets: SupportTicket[] = [];
  selectedStatus: SupportTicketStatus | 'All' = 'All';
  loading = false;
  hasError = false;
  totalRecords = 0;
  currentPage = 1;
  currentPageSize = SUPPORT_TICKET_PAGE_SIZE;
  errorMessage = 'Unable to load support tickets right now.';

  private readonly destroy$ = new Subject<void>();

  private sort: MatSort;

  @ViewChild(MatSort)
  set matSort(sort: MatSort) {
    this.sort = sort;
    this.dataSource.sort = sort;
  }

  constructor(
    private readonly supportTicketService: SupportTicketService,
    private readonly router: Router,
    private readonly liveAnnouncer: LiveAnnouncer
  ) {}

  ngOnInit(): void {
    this.currentPageSize = this.getStoredPageSize();
    this.currentPage = this.getStoredPageNumber();

    this.searchControl.valueChanges
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(() => this.reloadTickets());

    this.loadTickets(this.currentPage, true);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onStatusChange(): void {
    this.reloadTickets();
  }

  reloadTickets(): void {
    this.currentPage = 1;
    this.storePageNumber(this.currentPage);
    this.loadTickets(1, true);
  }

  openCreateTicket(): void {
    this.router.navigate(['/support-tickets/new']);
  }

  openTicket(ticket: SupportTicket): void {
    this.router.navigate(['/support-tickets', ticket.id]);
  }

  retry(): void {
    this.reloadTickets();
  }

  announceSortChange(sortState: Sort): void {
    if (sortState.direction) {
      this.liveAnnouncer.announce(`Sorted ${sortState.direction}ending`);
      return;
    }

    this.liveAnnouncer.announce('Sorting cleared');
  }

  getStatusClass(status: string): string {
    switch (status) {
      case SupportTicketStatus.WorkInProgress:
        return 'status-pill in-progress';
      case SupportTicketStatus.Completed:
        return 'status-pill completed';
      default:
        return 'status-pill received';
    }
  }

  trackByTicket(_: number, ticket: SupportTicket): string {
    return ticket.id;
  }

  get totalPages(): number {
    return Math.max(Math.ceil(this.totalRecords / this.currentPageSize), 1);
  }

  get pageNumbers(): number[] {
    return Array.from({ length: this.totalPages }, (_, index) => index + 1);
  }

  get pageRangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.currentPageSize + 1;
  }

  get pageRangeEnd(): number {
    return this.totalRecords === 0
      ? 0
      : Math.min(this.currentPage * this.currentPageSize, this.totalRecords);
  }

  onPageSelect(page: number): void {
    if (!page || page === this.currentPage) {
      return;
    }

    this.loadTickets(page, true);
  }

  onPageSizeSelect(pageSize: number): void {
    if (!pageSize || pageSize === this.currentPageSize) {
      return;
    }

    this.currentPageSize = pageSize;
    this.storePageSize(pageSize);
    this.loadTickets(1, true);
  }

  goToPreviousPage(): void {
    if (this.currentPage <= 1) {
      return;
    }

    this.loadTickets(this.currentPage - 1, true);
  }

  goToNextPage(): void {
    if (this.currentPage >= this.totalPages) {
      return;
    }

    this.loadTickets(this.currentPage + 1, true);
  }

  private loadTickets(page: number, reset: boolean): void {
    this.hasError = false;
    this.loading = true;

    this.supportTicketService
      .listTickets({
        page,
        limit: this.currentPageSize,
        search: this.searchControl.value || '',
        status: this.selectedStatus,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.currentPage = response.page;
          this.currentPageSize = response.limit || this.currentPageSize;
          this.storePageSize(this.currentPageSize);
          this.storePageNumber(this.currentPage);
          this.totalRecords = response.total;
          this.tickets = response.items;
          this.dataSource = new MatTableDataSource(this.tickets);
          if (this.sort) {
            this.dataSource.sort = this.sort;
          }
          this.loading = false;
        },
        error: () => {
          this.loading = false;
          this.hasError = true;
        },
      });
  }

  private getStoredPageSize(): number {
    try {
      const storedValue = sessionStorage.getItem(
        SupportTicketListComponent.PAGE_SIZE_STORAGE_KEY
      );
      const parsedValue = storedValue ? Number(storedValue) : NaN;

      return this.pageSizeOptions.includes(parsedValue)
        ? parsedValue
        : SUPPORT_TICKET_PAGE_SIZE;
    } catch {
      return SUPPORT_TICKET_PAGE_SIZE;
    }
  }

  private getStoredPageNumber(): number {
    try {
      const storedValue = sessionStorage.getItem(
        SupportTicketListComponent.PAGE_NUMBER_STORAGE_KEY
      );
      const parsedValue = storedValue ? Number(storedValue) : NaN;

      return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : 1;
    } catch {
      return 1;
    }
  }

  private storePageSize(pageSize: number): void {
    try {
      sessionStorage.setItem(
        SupportTicketListComponent.PAGE_SIZE_STORAGE_KEY,
        String(pageSize)
      );
    } catch {
      // Ignore storage failures and fall back to in-memory state.
    }
  }

  private storePageNumber(pageNumber: number): void {
    try {
      sessionStorage.setItem(
        SupportTicketListComponent.PAGE_NUMBER_STORAGE_KEY,
        String(pageNumber)
      );
    } catch {
      // Ignore storage failures and fall back to in-memory state.
    }
  }
}